import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@ui/components/base/dialog';
import { InputGroup, InputGroupAddon } from '@ui/components/base/input-group';
import { CheckIcon, SearchIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

type CommandProps = React.ComponentProps<typeof CommandPrimitive>;

const Command = ({ className, ...props }: CommandProps) => (
  <CommandPrimitive
    data-slot="command"
    className={cn(
      'flex size-full flex-col overflow-hidden rounded-xl! bg-popover p-1 text-popover-foreground',
      className
    )}
    {...props}
  />
);

interface CommandDialogProps extends Omit<React.ComponentProps<typeof Dialog>, 'children'> {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
  children: React.ReactNode;
}

const CommandDialog = ({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = false,
  ...props
}: CommandDialogProps) => (
  <Dialog {...props}>
    <DialogHeader className="sr-only">
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{description}</DialogDescription>
    </DialogHeader>
    <DialogContent
      className={cn('top-1/3 translate-y-0 overflow-hidden rounded-xl! p-0', className)}
      showCloseButton={showCloseButton}
    >
      {children}
    </DialogContent>
  </Dialog>
);

interface CommandInputProps extends React.ComponentProps<typeof CommandPrimitive.Input> {
  wrapperClassName?: string;
  endAddon?: React.ReactNode;
}

const CommandInput = React.forwardRef<HTMLInputElement, CommandInputProps>(
  ({ className, wrapperClassName, endAddon, ...props }, ref) => (
    <div data-slot="command-input-wrapper" className="p-1 pb-0">
      <InputGroup
        className={cn(
          'h-8! rounded-lg! border-input/30 bg-input/30 shadow-none!',
          '*:data-[slot=input-group-addon]:pl-2!',
          wrapperClassName
        )}
      >
        <CommandPrimitive.Input
          ref={ref}
          data-slot="command-input"
          className={cn('w-full text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50', className)}
          {...props}
        />
        <InputGroupAddon>
          <SearchIcon className="size-4 shrink-0 opacity-50" />
        </InputGroupAddon>
        {endAddon && <InputGroupAddon align="inline-end">{endAddon}</InputGroupAddon>}
      </InputGroup>
    </div>
  )
);
CommandInput.displayName = 'CommandInput';

type CommandListProps = React.ComponentProps<typeof CommandPrimitive.List>;

const CommandList = ({ className, ...props }: CommandListProps) => (
  <CommandPrimitive.List
    data-slot="command-list"
    className={cn('no-scrollbar max-h-72 scroll-py-1 overflow-x-hidden overflow-y-auto outline-none', className)}
    {...props}
  />
);

type CommandEmptyProps = React.ComponentProps<typeof CommandPrimitive.Empty>;

const CommandEmpty = ({ className, ...props }: CommandEmptyProps) => (
  <CommandPrimitive.Empty data-slot="command-empty" className={cn('py-6 text-center text-sm', className)} {...props} />
);

type CommandGroupProps = React.ComponentProps<typeof CommandPrimitive.Group>;

const CommandGroup = ({ className, ...props }: CommandGroupProps) => (
  <CommandPrimitive.Group
    data-slot="command-group"
    className={cn(
      'overflow-hidden p-1 text-foreground',
      '**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5',
      '**:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium',
      '**:[[cmdk-group-heading]]:text-muted-foreground',
      className
    )}
    {...props}
  />
);

type CommandSeparatorProps = React.ComponentProps<typeof CommandPrimitive.Separator>;

const CommandSeparator = ({ className, ...props }: CommandSeparatorProps) => (
  <CommandPrimitive.Separator
    data-slot="command-separator"
    className={cn('-mx-1 h-px bg-border', className)}
    {...props}
  />
);

type CommandItemProps = React.ComponentProps<typeof CommandPrimitive.Item>;

const CommandItem = ({ className, children, ...props }: CommandItemProps) => (
  <CommandPrimitive.Item
    data-slot="command-item"
    className={cn(
      'group/command-item relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm font-normal',
      'outline-hidden select-none in-data-[slot=dialog-content]:rounded-lg!',
      'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
      'data-[selected=true]:bg-muted data-[selected=true]:text-foreground',
      "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      'data-[selected=true]:*:[svg]:text-foreground',
      className
    )}
    {...props}
  >
    {children}
    <CheckIcon
      className={cn(
        'ml-auto opacity-0',
        'group-has-data-[slot=command-shortcut]/command-item:hidden',
        'group-data-[checked=true]/command-item:opacity-100'
      )}
    />
  </CommandPrimitive.Item>
);

type CommandShortcutProps = React.ComponentProps<'span'>;

const CommandShortcut = ({ className, ...props }: CommandShortcutProps) => (
  <span
    data-slot="command-shortcut"
    className={cn(
      'ml-auto text-xs tracking-widest text-muted-foreground group-data-[selected=true]/command-item:text-foreground',
      className
    )}
    {...props}
  />
);

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
